import { Modal, Box, Button, Typography } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFloppyDisk, faRotateLeft, faSpinner, faTriangleExclamation, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

const UnsavedChangesModal = ({
  open,
  onSave,
  onDiscard,
  onClose,
  saving = false,
  discarding = false,
  title,
  message,
  error,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Modal open={open} onClose={() => {}}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'white',
          borderRadius: '10px',
          boxShadow: 24,
          p: isMobile ? 2.5 : 3.5,
          width: isMobile ? '80%' : '90%',
          maxWidth: 520,
          outline: 'none',
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={onClose}
          disabled={saving || discarding}
          aria-label={t('close', 'Close')}
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            background: 'none',
            border: 'none',
            fontSize: 20,
            color: '#888',
            cursor: saving || discarding ? 'default' : 'pointer',
            padding: '2px 6px',
            borderRadius: '6px',
            transition: 'all 0.2s ease',
            lineHeight: 1,
            opacity: saving || discarding ? 0.6 : 1,
            '&:hover': {
              background: saving || discarding ? 'none' : '#f0f0f0',
              color: saving || discarding ? '#888' : '#333',
            },
          }}
        >
          <FontAwesomeIcon icon={faXmark} />
        </Box>

        <Box sx={{ pr: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: '#5616ea', fontSize: 18, flexShrink: 0 }} />
            <h3 style={{ margin: 0 }}>
              {title || t('unsaved_changes_title', 'Unsaved changes')}
            </h3>
          </Box>
          <Typography sx={{ color: '#5f6368', fontSize: 15, lineHeight: 1.5 }}>
            {message || t('unsaved_changes_modal_message', 'You have changes that are not saved yet. Save them before leaving, or discard them to continue without applying them.')}
          </Typography>
          {error ? (
            <Box
              sx={{
                mt: 1.5,
                px: 1.5,
                py: 1,
                borderRadius: '6px',
                backgroundColor: '#fdecea',
                border: '1px solid #f5c2c0',
              }}
            >
              <Typography sx={{ color: '#b71c1c', fontSize: 14, lineHeight: 1.4 }}>
                {error}
              </Typography>
            </Box>
          ) : null}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3, flexWrap: 'wrap' }}>
          <Button
            onClick={onDiscard}
            variant="outlined"
            disabled={saving || discarding}
            startIcon={discarding ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faRotateLeft} />}
            sx={{
              color: '#5616ea',
              borderColor: '#5616ea',
              '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' },
            }}
          >
            {t('discard_changes') || 'Discard Changes'}
          </Button>

          <Button
            onClick={onSave}
            variant="contained"
            disabled={saving || discarding}
            startIcon={saving ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faFloppyDisk} />}
            sx={{
              backgroundColor: '#5616ea',
              '&:hover': { backgroundColor: '#4a14d6' },
            }}
          >
            {t('save') || 'Save'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default UnsavedChangesModal;
